import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { LocalStorageService } from '../../../shared/services/storage/local-storage.service';
import {
  ResolveFn,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { PanelService } from '../../../shared/services/api/panel.service';
import { StoreRegionService } from '../../../shared/services/store-region.service';
import { AppStateService } from '../../../shared/services/app-state.service';
import { HomeV2 } from '../../../shared/models/home.model';
import { StoreCountry } from '../../../shared/models/location.model';

export interface HomepageResolvedData {
  homepage: HomeV2;
  countryCode: string;
  region: StoreCountry;
}

export const homepageResolver: ResolveFn<
  HomepageResolvedData | UrlTree
> = (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
): Observable<HomepageResolvedData | UrlTree> => {
  const router = inject(Router);
  const panelService = inject(PanelService);
  const storeRegionService = inject(StoreRegionService);
  const appStateService = inject(AppStateService);
  const platformId = inject(PLATFORM_ID);
  const localStorage = inject(LocalStorageService);
  const isBrowser = isPlatformBrowser(platformId);

  // Extract countryCode from route params (not parent route for :countryCode route)
  const countryCode = route.params['countryCode'] ?? '';

  if (!countryCode) {
    return of(router.parseUrl('/not-found'));
  }

  const countryCodeUpper = countryCode.toUpperCase();

  // Check AppState cache first (skip on browser when logged in — needs auth-specific payload)
  const cachedHomepage = appStateService.getHomepageByCountry(countryCodeUpper);
  const cachedRegion = appStateService.getSelectedRegion();
  const skipAppCacheOnClient =
    isBrowser && localStorage.isLoggedIn;

  // If we have cached data and the region matches, use it
  if (
    !skipAppCacheOnClient &&
    cachedHomepage &&
    cachedRegion &&
    cachedRegion.code === countryCodeUpper
  ) {
    return of({
      homepage: cachedHomepage,
      countryCode: countryCodeUpper,
      region: cachedRegion
    } as HomepageResolvedData);
  }

  // Fetch region list
  return panelService.getRegionList({ ordering: 'name' }).pipe(
    take(1),
    catchError(() => of([] as StoreCountry[])),
    map((raw: StoreCountry[] | { results?: StoreCountry[] }) =>
      Array.isArray(raw) ? raw : (raw?.results ?? [])
    ),
    switchMap((regions: StoreCountry[]) => {
      // Find the region by country code
      const region = regions.find((r) => r.code === countryCodeUpper);

      // Validate region exists
      if (!region) {
        return of(router.parseUrl('/not-found'));
      }

      // Validate region has homepage
      if (!region.has_homepage) {
        return of(router.parseUrl('/not-found'));
      }

      // Update AppState with regions
      appStateService.setStoreCountries(regions);
      
      // Set the region in StoreRegionService
      storeRegionService.setRegion(region);

      // Fetch homepage data
      return panelService.getHomeListV2(countryCodeUpper, { clearCache: true }).pipe(
        take(1),
        map((homepage: HomeV2) => {
          if (!homepage || !homepage.modules) {
            return router.parseUrl('/not-found');
          }

          // Cache homepage data
          appStateService.setHomepageByCountry(countryCodeUpper, homepage);

          return {
            homepage,
            countryCode: countryCodeUpper,
            region
          } as HomepageResolvedData;
        }),
        catchError(() => of(router.parseUrl('/not-found')))
      );
    }),
    catchError(() => of(router.parseUrl('/not-found'))),
    take(1)
  );
};
